<template>
  <div id="subscription-list">
    <!-- CREATE DIALOG -->
    <v-dialog v-model="addRecordPrompt" persistent max-width="85%">
      <subscription-type-create
        pageTitle="Create Subscription Type"
        pageDescription="Add a new fee for a JCI year"
        :addRecordPrompt="addRecordPrompt"
        @hideAddRecordPrompt="hideAddRecordPrompt"
        v-if="addRecordPrompt"
      />
    </v-dialog>

    <!-- EDIT DIALOG -->
    <v-dialog v-model="editRecordPrompt" persistent max-width="85%">
      <subscription-type-edit
        pageTitle="Edit Subscription Type"
        pageDescription="Change the fee, dates, penalty or advance settings"
        :editRecordPrompt="editRecordPrompt"
        @hideEditRecordPrompt="hideEditRecordPrompt"
        :recordIndex="recordToEdit"
        v-if="editRecordPrompt"
      />
    </v-dialog>

    <v-card elevation="8" class="rounded-xl st-list-card">
      <!-- HEADER -->
      <v-card-title class="st-list-header white--text pa-5">
        <div class="d-flex align-center w-100 flex-wrap">
          <v-icon large color="white" class="mr-3"
            >mdi-credit-card-settings</v-icon
          >
          <div>
            <h2 class="text-h5 font-weight-bold mb-0">{{ PageTitle }}</h2>
            <div class="text-subtitle-2 st-list-sub">{{ PageDescription }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            @click.prevent="addRecordPrompt = true"
            color="#ff9800"
            elevation="6"
            rounded
            class="font-size-h6 px-5 mr-3 white--text"
          >
            <v-icon left>mdi-plus-circle</v-icon>
            Create
          </v-btn>
          <v-btn
            @click.prevent="refreshPageData"
            color="#1db954"
            elevation="6"
            rounded
            class="font-size-h6 px-5 white--text"
          >
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text class="pa-5">
        <!-- LOADING -->
        <div class="d-flex justify-center mb-3" v-if="LoadingFlag">
          <b-spinner variant="primary" type="grow" label="Loading..." />
        </div>

        <!-- FILTERS -->
        <v-form
          ref="form2"
          v-model="valid1"
          lazy-validation
          v-if="!StartupLoadingFlag"
        >
          <v-row>
            <v-col cols="12" sm="4" md="3">
              <h6 class="mb-2 font-weight-bold">
                <span class="text-danger">*</span> Year
              </h6>
              <v-autocomplete
                @change="getTableRecords"
                :reduce="(option) => option.value"
                :items="JciYearCodeOptions"
                :rules="JciYearCodeRules"
                :loading="JciYearCodeOptionsLoading"
                v-model="JciYearCode"
                placeholder="Select Year"
                prepend-inner-icon="mdi-calendar"
                dense
                clearable
                required
                outlined
              />
            </v-col>
            <v-col cols="12" sm="8" md="5" v-if="rows.length > 0">
              <h6 class="mb-2 font-weight-bold">Search</h6>
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                placeholder="Search by description, category, amount..."
                outlined
                dense
                clearable
              />
            </v-col>
          </v-row>
        </v-form>

        <!-- STATS -->
        <v-row v-if="!SearchFlag && rows.length > 0" class="mb-2">
          <v-col cols="6" sm="3" v-for="(s, i) in StatItems" :key="i">
            <v-card
              outlined
              class="st-stat pa-3"
              :style="{ borderLeftColor: s.color }"
            >
              <div class="text-caption grey--text text--darken-1">
                {{ s.title }}
              </div>
              <div class="text-h6 font-weight-bold" :style="{ color: s.color }">
                {{ s.value }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Skeleton for Search Loading -->
        <v-skeleton-loader
          type="table-tbody"
          v-if="SearchFlag && !StartupLoadingFlag"
        />

        <!-- No Records -->
        <v-alert
          v-if="!SearchFlag && !StartupLoadingFlag && rows.length == 0"
          type="info"
          text
          outlined
          class="text-center"
        >
          No subscription types found for the selected year. Click Create to add
          one.
        </v-alert>

        <!-- TABLE -->
        <v-data-table
          v-if="!SearchFlag && rows.length > 0"
          class="elevation-2 rounded-lg st-table"
          v-model="selected"
          :headers="tableColumns1"
          :items="rows"
          :items-per-page="50"
          :search="search"
          item-key="YearwiseContributionId"
          :single-select="false"
          :show-select="TableSelectFlag"
          :footer-props="{
            'items-per-page-options': [10, 20, 30, 40, 50],
          }"
        >
          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="warning"
                  @click="editAlert(item)"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-file-document-edit</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
          </template>

          <!-- Status -->
          <template v-slot:item.ActiveStatusTxt="{ item }">
            <v-chip
              small
              :color="getActiveStatusColor(item.ActiveStatusTxt)"
              dark
            >
              {{ item.ActiveStatusTxt }}
            </v-chip>
          </template>

          <!-- Description -->
          <template v-slot:item.Description="{ item }">
            <span class="font-weight-bold">{{ item.Description }}</span>
          </template>

          <!-- Payment Category -->
          <template v-slot:item.PaymentCategoryTxt="{ item }">
            <v-chip
              small
              outlined
              :color="getPaymentCategoryColor(item.PaymentCategory)"
            >
              {{ item.PaymentCategoryTxt }}
            </v-chip>
          </template>

          <!-- Period -->
          <template v-slot:item.StartDateTxt="{ item }">
            <div class="text-no-wrap">{{ item.StartDateTxt }}</div>
            <div class="text-no-wrap text-caption grey--text">
              to {{ item.EndDateTxt }}
            </div>
          </template>

          <!-- Late fee end date -->
          <template v-slot:item.FineEndDateTxt="{ item }">
            <span class="text-no-wrap">{{ item.FineEndDateTxt }}</span>
          </template>

          <!-- Amounts -->
          <template v-slot:item.ContributionAmount="{ item }">
            <span class="text-no-wrap">{{
              formatAmount(item.ContributionAmount)
            }}</span>
          </template>
          <template v-slot:item.WelfareAmount="{ item }">
            <span class="text-no-wrap">{{
              formatAmount(item.WelfareAmount)
            }}</span>
          </template>
          <template v-slot:item.Tax="{ item }">
            <span class="text-no-wrap">{{ item.Tax }}%</span>
          </template>
          <template v-slot:item.TotalAmount="{ item }">
            <span class="text-no-wrap font-weight-bold primary--text">
              {{ formatAmount(item.TotalAmount) }}
            </span>
          </template>
          <template v-slot:item.FineAmount="{ item }">
            <span
              class="text-no-wrap"
              :class="
                toNumber(item.FineAmount) > 0
                  ? 'orange--text text--darken-2'
                  : 'grey--text'
              "
            >
              {{
                toNumber(item.FineAmount) > 0
                  ? formatAmount(item.FineAmount)
                  : "No penalty"
              }}
            </span>
          </template>

          <!-- Advance -->
          <template v-slot:item.IsAdvanceTxt="{ item }">
            <v-chip
              small
              :color="item.IsAdvance == 1 ? 'deep-purple' : 'grey'"
              dark
            >
              {{ item.IsAdvance == 1 ? "Advance" : "Full payment" }}
            </v-chip>
          </template>
          <template v-slot:item.BalanceAmount="{ item }">
            <span
              class="text-no-wrap"
              :class="item.IsAdvance == 1 ? '' : 'grey--text'"
            >
              {{ item.IsAdvance == 1 ? formatAmount(item.BalanceAmount) : "-" }}
            </span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import SubscriptionTypeCreate from "@/view/pages/erp/subscription-type/SubscriptionTypeCreate.vue";
import SubscriptionTypeEdit from "@/view/pages/erp/subscription-type/SubscriptionTypeEdit.vue";

export default {
  mixins: [common],
  components: {
    SubscriptionTypeCreate,
    SubscriptionTypeEdit,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      TableSelectFlag: false,

      rows: [],
      tableColumns1: [],
      selected: [],
      search: "",

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      zone: {},
      lom: {},

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      name: "",
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 10) || "Name must be less than 10 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      FromZoneCodeRules: [(v) => !!v || "Zone is required"],
      FromZoneCode: "",
      FromZoneCodeOptions: [],
      FromZoneCodeOptionsLoading: false,

      FromLomCodeRules: [(v) => !!v || "LO is required"],
      FromLomCode: "",
      FromLomCodeOptions: [],
      FromLomCodeOptionsLoading: false,

      ToZoneCodeRules: [(v) => !!v || "Zone is required"],
      ToZoneCode: "",
      ToZoneCodeOptions: [],
      ToZoneCodeOptionsLoading: false,

      ToLomCodeRules: [(v) => !!v || "LO is required"],
      ToLomCode: "",
      ToLomCodeOptions: [],
      ToLomCodeOptionsLoading: false,

      SearchId: "",
      SearchIdRules: [(v) => !!v || "Search Type is required"],
      SearchIdOptions: [
        { value: "", text: "--Select Type--" },
        { value: 1, text: "By LO" },
        { value: 2, text: "By Membership Id" },
      ],

      MembershipId: "",
      MembershipIdRules: [(v) => !!v || "Membership Id is required"],

      YearHalfId: "",
      YearHalfIdRules: [(v) => !!v || "Year Half is required"],
      YearHalfIdOptions: [
        { value: "", text: "--Select Half--" },
        { value: "First", text: "First Half" },
        { value: "Second", text: "Second Half" },
      ],

      YearIdRules: [(v) => !!v || "Year is required"],
      YearId: "",
      YearIdOptions: [],
      YearIdOptionsLoading: false,

      ApprovalDescription: "",
      ApprovalDescriptionRules: [],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {
    StatItems() {
      var rows = this.rows;
      var active = rows.filter((e) => e.ActiveStatus == 1).length;
      var advance = rows.filter((e) => e.IsAdvance == 1).length;
      var new_member = rows.filter((e) => e.PaymentCategory == 3).length;
      return [
        { title: "Total fees", value: rows.length, color: "#0a79df" },
        { title: "Active", value: active, color: "#2e7d32" },
        { title: "Advance payment", value: advance, color: "#673ab7" },
        { title: "New Member fees", value: new_member, color: "#ef6c00" },
      ];
    },
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log("ZoneCode=" + ZoneCode);
        this.ZoneCode = ZoneCode;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.StartupLoadingFlag = false;
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
      this.getTableRecords();
    },
  },
  methods: {
    toNumber(value) {
      var number = parseFloat(value);
      return isNaN(number) ? 0 : number;
    },
    formatAmount(value) {
      return (
        "₹" +
        this.toNumber(value).toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      );
    },
    getPaymentCategoryColor(category) {
      return category == 3
        ? "orange darken-2"
        : category == 2
        ? "teal"
        : "blue";
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "membership_subscriptions",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.StartupLoadingFlag = true;
      this.getJciYearCodeOptions();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.getTableRecords();
      }
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      console.log({ flag });
      if (flag) {
        this.getTableRecords();
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    editAlert(tr) {
      console.log("editAlert called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        // var CityId = tr.CityId;
        // this.member = tr;
        this.recordToEdit = tr.YearwiseContributionId;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to edit";
        this.errorMessage(message);
      }
    },
    getJciYearCodeOptions() {
      this.LoadingFlag = true;
      this.JciYearCodeOptionsLoading = true;
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var YearCode = this.JciYearCode;
      console.log("YearCode=" + YearCode);

      this.rows = [];

      if (YearCode != "") {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-contribution/lists";
        var upload = {
          UserInterface: 2,
          YearCode: YearCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.rows = [];
        this.SearchFlag = true;
        this.LoadingFlag = true;

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SearchFlag = false;
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.rows = records.TableData;
              thisIns.TableSelectFlag = records.TableSelectFlag;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.SearchFlag = false;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Year should not be empty.";
        console.log("error=" + message);
      }
    },
    getZoneCodeOptions(source, destination) {
      console.log("getZoneCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      var CurrentYearId = this.CurrentYearId;
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", CurrentYearId=" +
          CurrentYearId
      );
      if (source != "" && destination != "") {
        this.LoadingFlag = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: CurrentYearId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getLomCodeOptions(source, destination, ZoneCode) {
      console.log("getLomCodeOptions called");
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", ZoneCode=" +
          ZoneCode
      );
      var CurrentYearId = this.CurrentYearId;
      if (ZoneCode != "" && CurrentYearId != "") {
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: CurrentYearId,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "Zone should not be empty";
        this.toast("error", message);
      }
    },
    getYearCodeOptions(source, destination) {
      console.log("getYearCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      // var CurrentYearId = this.CurrentYearId;
      var CurrentYearId = 1;
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", CurrentYearId=" +
          CurrentYearId
      );
      if (source != "" && destination != "") {
        this.LoadingFlag = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/jci-year/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    searchForm() {
      console.log("searchForm is called");

      var validate1 = this.$refs.form2.validate();
      var SearchId = this.SearchId;
      var UserInterface = SearchId == 1 ? 1 : 3;
      console.log(
        "validate1=" +
          validate1 +
          ", SearchId=" +
          SearchId +
          ", UserInterface=" +
          UserInterface
      );

      if (validate1) {
        this.SearchFlag = true;
        this.rows = [];
        this.zone = {};
        this.lom = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members";
        var upload = {
          UserInterface: UserInterface,
          additional: {
            MembershipId: this.MembershipId,
            LomCode: this.FromZoneCode,
            LomCode: this.FromLomCode,
          },
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SearchFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.toast("success", output);
              thisIns.rows = records;
              thisIns.zone = response.data.zone;
              thisIns.lom = response.data.lom;
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form2.validate();
      var validate2 = this.$refs.form3.validate();
      console.log("validate1=" + validate1 + "validate2=" + validate2);

      var members = this.selected;
      var n1 = members.length;
      console.log("n1=" + n1 + ", members=" + JSON.stringify(members));

      // this.rows = [];

      if (validate1 && validate2 && n1 > 0) {
        this.SubmitFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members/transfer";
        var upload = {
          UserInterface: this.SearchId,
          FromZoneCode: this.FromZoneCode,
          FromLomCode: this.FromLomCode,
          MembershipId: this.MembershipId,
          ToZoneCode: this.ToZoneCode,
          ToLomCode: this.ToLomCode,
          YearId: this.YearId,
          YearHalf: this.YearHalfId,
          ApprovalDescription: this.ApprovalDescription,
          Members: members,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.toast("success", output);
              thisIns.resetForm();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
          });
      } else {
        var message = "";
        if (!validate1 || !validate2) {
          message += "Kindly fill the required fields";
        }
        if (n1 == 0) {
          message += "Atleast one member is required to transfer";
        }
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss">
#subscription-list {
  .st-list-card {
    border-radius: 12px;
    overflow: hidden;
  }

  .st-list-header {
    background: linear-gradient(90deg, #004e92, #0a79df);

    h2 {
      color: white !important;
    }
  }

  .st-list-sub {
    opacity: 0.85;
  }

  .st-stat {
    border-radius: 10px !important;
    border-left: 4px solid #0a79df !important;
  }

  .st-table {
    .v-data-table__wrapper > table > thead > tr > th {
      background: #eef5fd;
      color: #004e92 !important;
      font-size: 13px;
      white-space: nowrap;
    }

    .v-data-table__wrapper > table > tbody > tr > td {
      font-size: 13px;
    }
  }

  .text-danger {
    color: #f44336;
    font-weight: bold;
  }

  h6 {
    font-size: 14px;
    letter-spacing: 0.5px;
    color: #333;
  }
}
</style>
